import {ItemModelType, WorkspaceModelType} from "@stores/WorkspaceStore";
import intl from "react-intl-universal";

import type {AppState} from "@stores/AppState";
import {ItemType} from "../../API";
import {_apply_affected_items} from "./_apply_affected_items";
import {updateDocumentArchived} from "../../sync/DocumentApiRequests";
import {
    updateWorkspaceArchivedIdsAdd, updateWorkspaceArchivedIdsRemove, updateWorkspaceChildIdsAdd,
    updateWorkspaceChildIdsMoveToArchive,
    updateWorkspaceChildIdsRestoreFromArchive
} from "../workspace/updateWorkspace";
import {updateItemChildIdsAdd, updateItemChildIdsRemove, updateItemDB} from "./updateItem";
import {selectItemPlain} from "../ui/selectItem";
import {makeReverseClass, UndoableAction} from "../common/UndoableAction";
import {undoableFunctionCall} from "../common/undoableFunctionCall";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {CrudServices} from "../../platform/services/types/ServiceDependencies";
import {DataSourceManager} from "@stores/dataSources/DataSourceManager";
import {UserSessionState} from "@stores/UserSessionState";
import {WsServerConnection} from "@stores/serverConnection/wsServerConnection";
import {
    captureStatusProvenance,
    clearStatusProvenance,
    getLiveStatusRestoreParents,
    getStatusSourceLibrary
} from "./statusProvenance";


export class UndoableMoveToArchive implements UndoableAction {
    private services: CrudServices;
    private appState: AppState;
    private itemId: string;
    private workspaceMST: WorkspaceModelType;

    constructor(
        services: CrudServices,
        appState: AppState,
        itemId: string,
        workspaceMST: WorkspaceModelType
    ) {
        this.services = services;
        this.appState = appState;
        this.itemId = itemId;
        this.workspaceMST = workspaceMST;
    }

    execute(): Promise<any> {
        return moveToArchive(this.services, this.appState, this.itemId, this.workspaceMST);
    }

    undo(): Promise<any> {
        return restoreFromArchive(this.services, this.appState, this.itemId, this.workspaceMST);
    }
}

export const UndoableRestoreFromArchive = makeReverseClass(UndoableMoveToArchive);

export const moveToArchiveUndoable = (
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoableMoveToArchive(services, appState, itemId, workspace);
    return undoableFunctionCall(undoableAction, intl.get('snackbar.archived').d('Page archived.'));
}

export const restoreFromArchiveUndoable = (
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoableRestoreFromArchive(services, appState, itemId, workspace);
    return undoableFunctionCall(undoableAction, intl.get('snackbar.restore_from_archive').d('Page restored.'))
}

export function moveToArchive(
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType,
    level = 0,
    undoableActionGroup?: UndoableActionGroup
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableMoveToArchive(services, appState, itemId, workspace);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _moveToArchive(services, appState, itemId, workspace, level);
    }
}

function _moveToArchive(
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType,
    level = 0
) {
    let wsDbChange: Promise<any> = Promise.resolve();
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    const wsUpdateServices = {...services, dsManager};

    if (workspace.selectedId === itemId) {
        selectItemPlain(appState.rootStore, workspace, null);
    }

    const itemMST = workspace.items.get(itemId)!;
    if (itemMST.archived) {
        return Promise.resolve();
    }
    captureStatusProvenance(workspace, itemMST);
    itemMST.setArchived(true);

    const waitAffected = _apply_affected_items(workspace, itemMST, async (item) => {
        // @ts-ignore
        await moveToArchive(services, appState, item.id, workspace, level + 1);
    }).catch(logErrorToSentry);

    if (level === 0) {
        if (workspace.childIds.includes(itemId)) {
            wsDbChange = updateWorkspaceChildIdsMoveToArchive(wsUpdateServices, workspace, itemId);
        } else {
            wsDbChange = updateWorkspaceArchivedIdsAdd(wsUpdateServices, workspace, itemId);
        }
        for (const parentId of itemMST.parentIds) {
            wsDbChange = wsDbChange.then(() => updateItemChildIdsRemove(services, dsManager, parentId, itemId, workspace));
        }
    }

    const waitDbChange = setArchiveDB(services, dsManager, userState, workspace.id, itemMST, true).catch(logErrorToSentry);

    return Promise.all([waitDbChange, waitAffected, wsDbChange]);
}

export function restoreFromArchive(
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType,
    level = 0
) {
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    const wsUpdateServices = {...services, dsManager};
    let wsDbChange: Promise<any> = Promise.resolve();
    const itemMST = workspace.items.get(itemId)!;
    itemMST.setArchived(false);

    const waitAffected = _apply_affected_items(workspace, itemMST, async (item) => {
        await restoreFromArchive(services, appState, item.id, workspace, level + 1);
    }).catch(logErrorToSentry);

    if (level === 0) {
        const restoreParents = getLiveStatusRestoreParents(workspace, itemMST);
        const sourceLibrary = getStatusSourceLibrary(workspace, itemMST);
        const targetParents = restoreParents.length > 0
            ? restoreParents
            : sourceLibrary
                ? [sourceLibrary]
                : [];

        if (targetParents.length > 0) {
            if (workspace.archivedIds.includes(itemId)) {
                wsDbChange = updateWorkspaceArchivedIdsRemove(wsUpdateServices, workspace, itemId);
            }
            for (const targetParent of targetParents) {
                if (!targetParent.childIds.includes(itemId)) {
                    wsDbChange = wsDbChange.then(() => updateItemChildIdsAdd(services, dsManager, targetParent, itemMST.id, workspace));
                }
            }
        } else {
            const targetLibrary = workspace.selectedLibrary;
            if (targetLibrary) {
                // Legacy/provenance-missing fallback for older archived rows.
                if (workspace.archivedIds.includes(itemId)) {
                    wsDbChange = updateWorkspaceArchivedIdsRemove(wsUpdateServices, workspace, itemId);
                }
                if (!targetLibrary.childIds.includes(itemId)) {
                    wsDbChange = wsDbChange.then(() => updateItemChildIdsAdd(services, dsManager, targetLibrary, itemMST.id, workspace));
                }
            } else {
                // Legacy workspace without libraries
                if (workspace.archivedIds.includes(itemId) && !workspace.childIds.includes(itemId)) {
                    wsDbChange = updateWorkspaceChildIdsRestoreFromArchive(wsUpdateServices, workspace, itemId);
                } else if (!workspace.childIds.includes(itemId)) {
                    wsDbChange = updateWorkspaceChildIdsAdd(wsUpdateServices, workspace, itemId);
                } else if (workspace.archivedIds.includes(itemId)) {
                    wsDbChange = updateWorkspaceArchivedIdsRemove(wsUpdateServices, workspace, itemId);
                }
            }
        }
        for (const parentId of itemMST.parentIds) {
            const parentMST = workspace.items.get(parentId)!;
            if (parentMST.archived) {
                wsDbChange = wsDbChange.then(() => updateItemChildIdsRemove(services, dsManager, parentId, itemId, workspace));
            }
        }
    }

    const waitDbChange = setArchiveDB(services, dsManager, userState, workspace.id, itemMST, false).catch(logErrorToSentry);
    clearStatusProvenance(itemMST);

    return Promise.all([waitDbChange, waitAffected, wsDbChange]);
}

async function setArchiveDB(
    services: CrudServices,
    dsManager: DataSourceManager,
    userState: UserSessionState,
    wsId: string,
    itemMST: ItemModelType,
    value: boolean
) {
    if (itemMST.type === ItemType.LEAF) {
        await setDocumentArchive(userState.wsServerConnection, itemMST, value);
    }

    await updateItemDB(services, dsManager, wsId, itemMST.id, {
        statusUpdatedBy: userState.currentUserId,
        archived: value,
        statusSourceLibraryId: value ? itemMST.statusSourceLibraryId : null,
        statusRestoreParentIds: value ? itemMST.statusRestoreParentIds.toJSON() : [],
    })
}

async function setDocumentArchive(wsServerConnection: WsServerConnection, itemDB: ItemModelType, value: boolean) {
    await updateDocumentArchived(
        wsServerConnection,
        {
            docId: itemDB.id!,
            archived: value,
        });
}
